import React from 'react';

import { aiMimeTypes, assertNeverSafely, clientSideMimeTypes } from '@aline/common';
import { z } from 'zod';

import { CodeAgentSkillElement } from './CodeAgentSkillElement';
import { CodeAlertElement } from './CodeAlertElement';
import { CodeDraftElement } from './CodeDraftElement';
import { CodeErrorElement } from './CodeErrorElement';
import { CodeHtmlDraftElement } from './CodeHtmlDraftElement';
import { CodePlaybookGenerationElement } from './CodePlaybookGenerationElement';
import { CodePlaybookRunElement } from './CodePlaybookRunElement';
import { CodeRedlineListElement } from './CodeRedlineListElement/CodeRedlineListElement';
import { CodeReportGenerationElement } from './CodeReportGenerationElement';
import { CodeSuggestionListElement } from './CodeSuggestionListElement/CodeSuggestionListElement';
import { CodeThinkingElement } from './CodeThinkingElement';
import { CodeToolCallElement } from './CodeToolCallElement';
import { CodeWorkflowElement } from './CodeWorkflowElement';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { extractClassWithPrefix } from '../../helpers/className-helpers';

const typeSchema = z.enum(clientSideMimeTypes);

/**
 * Renders a code element.
 */
export const CodeElement = React.memo(function CodeElement(
  props: MarkdownComponentProps
): JSX.Element | null {
  const rawType = extractClassWithPrefix(props.node, 'language-');
  const typeResult = typeSchema.safeParse(rawType);

  /**
   * If we don't recognize the type, just render the code block as is.
   *
   * This might happen if the AI legitimately generates a code block instead of
   * one of our custom code block renderers.
   */
  if (!typeResult.success) return <code {...props} />;

  const type = typeResult.data;

  switch (type) {
    case aiMimeTypes.jsonAgentSkill:
      return <CodeAgentSkillElement {...props} />;
    case aiMimeTypes.jsonAlert:
      return <CodeAlertElement {...props} />;
    case aiMimeTypes.jsonReportGeneration:
      return <CodeReportGenerationElement {...props} />;
    case aiMimeTypes.jsonToolCall:
      return <CodeToolCallElement {...props} />;
    case aiMimeTypes.jsonWorkflowResult:
      return <CodeWorkflowElement {...props} />;
    case aiMimeTypes.jsonlRedlineEdit:
      return <CodeRedlineListElement {...props} />;
    case aiMimeTypes.jsonlSuggestionList:
      /**
       * NOTE: This supports the old redlining output format. We can remove this
       * if we are blowing away the chat history; otherwise, we need to keep it
       * for compatibility.
       */
      return <CodeSuggestionListElement {...props} />;
    case aiMimeTypes.htmlDraftDocument:
      return <CodeHtmlDraftElement {...props} />;
    case aiMimeTypes.markdownDraftDocument:
      return <CodeDraftElement {...props} />;
    case aiMimeTypes.jsonError:
      return <CodeErrorElement {...props} />;
    case aiMimeTypes.jsonPlaybookGeneration:
      return <CodePlaybookGenerationElement {...props} />;
    case aiMimeTypes.jsonPlaybookRun:
      return <CodePlaybookRunElement {...props} />;
    case aiMimeTypes.textThinking:
      return <CodeThinkingElement {...props} />;
    default:
      assertNeverSafely(type);
      return <></>;
  }
});
